import React from "react";
import {
  Pagination as PaginationUI,
  PageCard,
  PageMiniCard,
  PageList,
} from "@caelor/ui";
import {
  Link,
  PageCardContainer,
  PageListContainer,
  PaginationWrapper,
} from "./styled";
import {
  NewPaginationTypesMapper,
  PaginationTypes,
} from "@Shared/Pagination-Types";
import { useTranslation } from "react-i18next";
import { htmlDecode } from "../usePages";
import { usePagesStore } from "../../../stores/use-page-store";
import { PageElements } from "../../../optics/components/drawers/settings/features/pages/defaults";

const LinkWrapper = ({ url, mode, openInNewTab, children }) => {
  if (mode === "view") {
    return (
      <Link
        href={url}
        target={openInNewTab ? "_blank" : "_top"}
        rel="noreferrer"
      >
        {children}
      </Link>
    );
  }
  return children;
};

const Mapper = ({ data, children }) => {
  return data.map((page) => children(page));
};

const Variant = (props) => {
  const { variant, children } = props;

  if (variant === "card") {
    return (
      <PageCardContainer {...props}>{children(PageCard)}</PageCardContainer>
    );
  }
  if (variant === "list") {
    return (
      <PageListContainer {...props}>{children(PageList)}</PageListContainer>
    );
  }
  if (variant === "mini_card") {
    return (
      <PageCardContainer {...props}>{children(PageMiniCard)}</PageCardContainer>
    );
  }
};

const Pagination = ({ data, pagination, queryPagination, children }) => {
  const { t } = useTranslation();

  const buildData = () => {
    if (pagination === PaginationTypes.INFINITE || queryPagination.disabled) {
      return Object.keys(data || {})?.flatMap((key) => data[key]) || [];
    }
    if (pagination === PaginationTypes.CAROUSEL) {
      return data[page] || [];
    }
  };

  if (queryPagination.disabled) return children(buildData());

  const { hasNextPage, hasPrevPage, onNext, onPrev, page } = queryPagination;

  return (
    <PaginationWrapper>
      {children(buildData())}
      <PaginationUI
        type={NewPaginationTypesMapper[pagination]}
        labels={{
          next:
            pagination === PaginationTypes.INFINITE
              ? t("show-more")
              : t("next"),
          prev: t("prev"),
        }}
        hasNext={hasNextPage}
        hasPrev={hasPrevPage}
        onNext={() => onNext()}
        onPrev={() => onPrev()}
      />
    </PaginationWrapper>
  );
};

function PagesPreview({ data, queryPagination, mode = "edit" }) {
  const { config } = usePagesStore((state) => ({
    config: state.data.config,
  }));

  const {
    variant,
    columns,
    pagination,
    activeElements,
    cardBorderRadius,
    openInNewTab,
  } = config;

  const shouldShowElement = (element) => activeElements?.includes(element);

  return (
    <Pagination
      data={data}
      pagination={pagination}
      queryPagination={queryPagination}
    >
      {(paginatedData) => (
        <Variant variant={variant} columns={columns}>
          {(PageComponent) => (
            <Mapper data={paginatedData}>
              {(page) => {
                return (
                  <LinkWrapper
                    url={page.url}
                    mode={mode}
                    openInNewTab={openInNewTab ?? true}
                  >
                    <PageComponent
                      {...page}
                      key={page.contentId}
                      title={page?.name}
                      description={
                        shouldShowElement(PageElements.DESCRIPTION)
                          ? htmlDecode(page.excerpt)
                          : null
                      }
                      authorName={
                        shouldShowElement(PageElements.AUTHOR)
                          ? page.createdBy
                          : null
                      }
                      authorUrl={page.authorUrl}
                      spaceName={
                        shouldShowElement(PageElements.SPACE)
                          ? page.space.name
                          : null
                      }
                      spaceUrl={page.spaceUrl}
                      modifiedDate={
                        shouldShowElement(PageElements.DATE)
                          ? page.lastModified
                          : null
                      }
                      comments={
                        shouldShowElement(PageElements.COMMENTS)
                          ? page.numberOfComments
                          : null
                      }
                      likes={
                        shouldShowElement(PageElements.LIKES)
                          ? page.numberOfLikes
                          : null
                      }
                      borderRadius={cardBorderRadius}
                    />
                  </LinkWrapper>
                );
              }}
            </Mapper>
          )}
        </Variant>
      )}
    </Pagination>
  );
}

export default PagesPreview;
